// @ts-nocheck
/**
 * Coerce a flat list or list-of-lists into layout-table headers + data rows.
 * First row / first element is the header row (CSV-style). DEC list-backed tables.
 */

export type ListTableMode = 'flat' | 'matrix' | 'empty' | 'invalid';

export type ListTableCell = string | number | boolean | null;

export interface ListTableCoercionIssue {
  severity: 'warning';
  code: string;
  message: string;
}

export interface ListTableCoercionResult {
  columnHeaders: string[];
  /** Data rows only (header row excluded). */
  rows: Array<{ values: readonly ListTableCell[] }>;
  mode: ListTableMode;
  issues: ListTableCoercionIssue[];
}

function cellToDisplay(value: unknown): ListTableCell {
  if (value == null) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function headerLabel(value: unknown, index: number): string {
  if (value == null || value === '') return `c${index}`;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : `c${index}`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    const s = JSON.stringify(value);
    return s.length > 0 ? s : `c${index}`;
  } catch {
    return `c${index}`;
  }
}

function normalizeRow(raw: unknown, columnCount: number): ListTableCell[] {
  const cells = Array.isArray(raw) ? raw : [raw];
  const out: ListTableCell[] = [];
  for (let i = 0; i < columnCount; i += 1) {
    out.push(i < cells.length ? cellToDisplay(cells[i]) : null);
  }
  return out;
}

function isMatrix(value: unknown[]): boolean {
  if (value.length === 0) return false;
  // First element array → matrix (including header-only).
  // If any later element is an array, treat as matrix and coerce scalars to 1-cell rows.
  if (Array.isArray(value[0])) return true;
  return value.some((el, i) => i > 0 && Array.isArray(el));
}

/**
 * Interpret `value` as a list-backed table payload.
 * - `[]` → empty
 * - `[h, a, b, …]` (no nested arrays) → 1 column, header `h`, data `a…`
 * - `[[h0,…], [r0c0,…], …]` → matrix; row 0 headers, rest data
 */
export function rowsFromListValue(value: unknown): ListTableCoercionResult {
  if (!Array.isArray(value)) {
    return {
      columnHeaders: [],
      rows: [],
      mode: 'invalid',
      issues: [
        {
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message: 'List-backed table source must be an array (flat list or list-of-lists).',
        },
      ],
    };
  }

  if (value.length === 0) {
    return { columnHeaders: [], rows: [], mode: 'empty', issues: [] };
  }

  if (isMatrix(value)) {
    const headerRaw = Array.isArray(value[0]) ? value[0] : [value[0]];
    const columnCount = Math.max(headerRaw.length, 1);
    const columnHeaders = Array.from({ length: columnCount }, (_, i) =>
      headerLabel(headerRaw[i], i),
    );
    const dataRows = value.slice(1).map((row) => ({
      values: normalizeRow(row, columnCount),
    }));
    return { columnHeaders, rows: dataRows, mode: 'matrix', issues: [] };
  }

  // Flat list: first element = header, rest = one-column cells.
  const columnHeaders = [headerLabel(value[0], 0)];
  const rows = value.slice(1).map((cell) => ({
    values: [cellToDisplay(cell)] as const,
  }));
  return { columnHeaders, rows: [...rows], mode: 'flat', issues: [] };
}

/** String preview grid for share/HTML (null → ''). */
export function previewRowsFromListTable(
  rows: Array<{ values: readonly ListTableCell[] }>,
): string[][] {
  return rows.map((row) => row.values.map((c) => (c == null ? '' : String(c))));
}
